import React, { useState } from "react";
import Model from "@tripian/model";
import { SvgIcons } from "@tripian/react";

interface IEmptyStepSlot {
  timeFrom: string;
  timeTo: string;
  alternatives: Model.Poi[];
  onSelectAlternative: (poi: Model.Poi) => void;
  onDeleteSlot?: () => void;
  isLoading?: boolean;
}

const EmptyStepSlot: React.FC<IEmptyStepSlot> = ({
  timeFrom,
  timeTo,
  alternatives,
  onSelectAlternative,
  onDeleteSlot,
  isLoading = false,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div className="w-full relative">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-xl">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 animate-spin text-primary-color" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium text-gray-600">Adding...</span>
          </div>
        </div>
      )}

      {/* Main slot card */}
      <div
        className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 bg-[#FDF8F6] flex items-center justify-between cursor-pointer hover:border-primary-color transition-colors relative"
        onClick={() => !isLoading && setShowSuggestions(!showSuggestions)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Delete button - appears on hover */}
        {isHovered && onDeleteSlot && !isLoading && (
          <button
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-md transition-colors z-10"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteSlot();
            }}
          >
            <SvgIcons.X fill="#fff" className="w-3 h-3" />
          </button>
        )}
        {/* Left: Add icon and text */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-gray-200">
            <SvgIcons.MapPin width="20" height="20" fill="#2B2B33" />
          </div>
          <span className="text-sm font-medium text-gray-700">Add a place</span>
        </div>

        {/* Center: Time range */}
        <div className="px-3 py-1 bg-white rounded-full border border-gray-200 text-sm text-gray-600">
          {timeFrom} - {timeTo}
        </div>

        {/* Right: See suggestions button */}
        <button
          className="px-4 py-2 bg-primary-color text-white text-sm font-medium rounded-full hover:opacity-90 transition-opacity disabled:opacity-50"
          disabled={isLoading}
          onClick={(e) => {
            e.stopPropagation();
            if (!isLoading) setShowSuggestions(!showSuggestions);
          }}
        >
          SEE SUGGESTIONS
        </button>
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && alternatives.length > 0 && (
        <div className="mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-3 border-b border-gray-100 bg-gray-50">
            <span className="text-sm font-medium text-gray-700">
              Suggestions ({alternatives.length})
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {alternatives.map((poi) => (
              <div
                key={poi.id}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => {
                  onSelectAlternative(poi);
                  setShowSuggestions(false);
                }}
              >
                {/* POI Image */}
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                  {poi.image?.url ? (
                    <img
                      src={poi.image.url}
                      alt={poi.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <SvgIcons.MapPin width="20" height="20" fill="#9CA3AF" />
                    </div>
                  )}
                </div>

                {/* POI Info */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900 truncate">
                    {poi.name}
                  </h4>
                  {poi.category && poi.category.length > 0 && (
                    <p className="text-xs text-gray-500 truncate">
                      {poi.category[0].name}
                    </p>
                  )}
                </div>

                {/* Add button */}
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary-color flex items-center justify-center">
                    <SvgIcons.Add size="1rem" fill="#fff" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state when no alternatives */}
      {showSuggestions && alternatives.length === 0 && (
        <div className="mt-2 bg-white border border-gray-200 rounded-xl p-4 text-center">
          <p className="text-sm text-gray-500">No suggestions available</p>
        </div>
      )}
    </div>
  );
};

export default EmptyStepSlot;